// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StartEndTime } from "@ces/App/Handlers/XNG/RequestedPickupTimes";
import { FulfillmentMethod } from "@ces/XDL/types";
import siteMessage from "@insite/client-framework/SiteMessage";

export interface IsRequestedFulfillmentDateValidParams {
    fulfillmentMethod: FulfillmentMethod | string;
    isGuest: boolean;
    selectedTime: string;
    requestedPickupDate?: Date | null;
    requestedDeliveryDate?: Date | null;
    isRequestDeliveryDateRequired: boolean;
    canRequestDeliveryDate: boolean;
}

export function isValidDate(blackOutDays: string[], currentDate: Date) {
    const todayDate = new Date();
    return (
        currentDate.getDay() !== 0 &&
        currentDate.getDay() !== 6 &&
        !blackOutDays.find((item: string) => new Date(item).toDateString() === currentDate.toDateString()) &&
        currentDate.getTime() > todayDate.getTime()
    );
}

/**
 *
 * @param date your date input
 * @returns YYYY-MM-DD formatted date
 */
export const getFormattedDate = (date: Date, reversed?: boolean) => {
    if (!date) {
        return "";
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return !reversed ? `${month}/${day}/${year}` : `${year}/${month}/${day}`;
};

export const getStartOfToday = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

export const isDateOnOrAfterToday = (date?: Date | null) => {
    if (!date) {
        return false;
    }
    return new Date(date).toDateString() >= new Date().toDateString();
};

/**
 * Add time to the date
 * param: date, time ("hh:mm AM/PM")
 *  */
export const setTime = (date: Date, time?: string) => {
    if (!time) {
        return date;
    }
    const newDate = new Date(date);
    const [_time, mer] = time.split(" ");
    const _timeArr = _time.split(":").map(Number);
    let hours = _timeArr[0];
    const minutes = _timeArr[1];

    if (mer?.toLowerCase() === "pm") {
        hours = hours + 12;
    }

    newDate.setHours(hours, minutes, 0);

    return newDate;
};

export const generatePickUpTimes = (openTime: string, closeTime: string) => {
    // Parse open and close times into hours and minutes
    const [openHour, openMinute] = openTime.split(":").map(Number);
    const [closeHour, closeMinute] = closeTime.split(":").map(Number);

    // Create Date objects for warehouse open and close times
    const warehouseOpenDate = new Date();
    warehouseOpenDate.setHours(openHour, openMinute, 0, 0);

    const warehouseCloseDate = new Date();
    warehouseCloseDate.setHours(closeHour, closeMinute, 0, 0);

    // Calculate one hour before closing time
    const oneHourBeforeClose = new Date(warehouseCloseDate);
    oneHourBeforeClose.setHours(oneHourBeforeClose.getHours() - 1);

    // Generate array of times from openTime until one hour before closeTime
    const times = [];
    const currentTime = new Date(warehouseOpenDate);

    while (currentTime <= oneHourBeforeClose) {
        // Format to 12-hour
        const formattedTime = currentTime.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        times.push(`${formattedTime} ${siteMessage("Custom_PickUp_TimeZone")}`);
        currentTime.setHours(currentTime.getHours() + 1); // Increment by 1 hour
    }

    return {
        availableTimes: times,
        startEndTime: { start: times[0], end: times[times.length - 1] },
    };
};

export const isCurrentTimeAvailable = (startEndTime?: StartEndTime) => {
    if (!startEndTime?.start || !startEndTime.end) {
        return false;
    }

    const now = new Date();
    const startDateTime = setTime(now, startEndTime.start);
    const endDateTime = setTime(now, startEndTime.end);

    return now >= startDateTime && now <= endDateTime;
};

export const validateRequestedDate = (date: Date, selectedTime: string, isGuest: boolean) =>
    isGuest ? isDateOnOrAfterToday(date) : setTime(date, selectedTime) >= new Date();

export const isRequestedFulfillmentDateValid = ({
    fulfillmentMethod,
    isGuest,
    selectedTime,
    requestedPickupDate,
    requestedDeliveryDate,
    isRequestDeliveryDateRequired,
    canRequestDeliveryDate,
}: IsRequestedFulfillmentDateValidParams) => {
    if (fulfillmentMethod === FulfillmentMethod.Ship) {
        const requiresDeliveryDate = isRequestDeliveryDateRequired && canRequestDeliveryDate;
        return requiresDeliveryDate
            ? validateRequestedDate(requestedDeliveryDate as Date, selectedTime, isGuest)
            : true;
    }

    return validateRequestedDate(requestedPickupDate as Date, selectedTime, isGuest);
};
